/**
 * Pixel art onto a different grid, without blending (docs/mockups/sizes-*.html).
 *
 * Each output pixel covers a patch of the source and takes the colour that
 * covers most of that patch, weighted by how much of each source pixel falls
 * inside it; it stays clear if less than half the patch is opaque. So every
 * colour out is one the art already used — the palette survives — and edges
 * stay hard. Shrinking loses the finest details a little unevenly and growing
 * doubles some pixels and not others, which is the price of not redrawing.
 *
 * Pure, on raw RGBA, so it can be tested without a canvas.
 */

export function resampleRGBA(
  src: Uint8ClampedArray,
  sw: number,
  sh: number,
  dw: number,
  dh: number,
): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(dw * dh * 4);
  const fx = sw / dw;
  const fy = sh / dh;
  const half = (fx * fy) / 2;
  const weights = new Map<number, number>();
  for (let y = 0; y < dh; y++) {
    const y0 = y * fy;
    const y1 = (y + 1) * fy;
    for (let x = 0; x < dw; x++) {
      const x0 = x * fx;
      const x1 = (x + 1) * fx;
      weights.clear();
      let opaque = 0;
      for (let sy = Math.floor(y0); sy < Math.min(sh, Math.ceil(y1)); sy++) {
        const hy = Math.min(y1, sy + 1) - Math.max(y0, sy);
        for (let sx = Math.floor(x0); sx < Math.min(sw, Math.ceil(x1)); sx++) {
          const k = (sy * sw + sx) * 4;
          if ((src[k + 3] ?? 0) < 128) continue;
          const w = (Math.min(x1, sx + 1) - Math.max(x0, sx)) * hy;
          opaque += w;
          const key = ((src[k] ?? 0) << 16) | ((src[k + 1] ?? 0) << 8) | (src[k + 2] ?? 0);
          weights.set(key, (weights.get(key) ?? 0) + w);
        }
      }
      if (opaque < half) continue;
      let best = 0;
      let most = -1;
      for (const [key, w] of weights) {
        if (w > most) {
          most = w;
          best = key;
        }
      }
      const p = (y * dw + x) * 4;
      out[p] = best >> 16;
      out[p + 1] = (best >> 8) & 255;
      out[p + 2] = best & 255;
      out[p + 3] = 255;
    }
  }
  return out;
}
